import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { MediaLibraryService, AnalysisProgressState, UploadProgress } from 'src/app/gen2ai/media-library.service';
import { MediaItem } from 'src/typings/media.interface';

type CoachAsset = {
  id: string;
  title: string;
  description: string;
  angles: string[];
  tags: string[];
  source: 'Gemini' | 'Custom Model';
  intensity: 'low' | 'medium' | 'high';
};

@Component({
  selector: 'app-tennis-coach',
  templateUrl: './tennis-coach.component.html',
  styleUrls: ['./tennis-coach.component.scss']
})
export class TennisCoachComponent implements OnInit, OnDestroy {
  @ViewChild('uploadInput') uploadInput?: ElementRef<HTMLInputElement>;

  private mediaSub?: Subscription;
  private analysisSub?: Subscription;

  videoClips: CoachAsset[] = [
    {
      id: 'clip-serve-001',
      title: 'Kick Serve - Wide Angle',
      description: 'Baseline wide shot highlighting shoulder tilt and ball toss arc.',
      angles: ['Baseline wide', 'Ball-toss overlay', 'Slow motion'],
      tags: ['serve', 'power', 'toss'],
      source: 'Gemini',
      intensity: 'high'
    },
    {
      id: 'clip-forehand-002',
      title: 'Forehand - Side Angle',
      description: 'Racquet lag timing and hip rotation aligned to impact frame.',
      angles: ['Side angle', 'Hip rotation map', 'Frame-by-frame'],
      tags: ['forehand', 'timing'],
      source: 'Custom Model',
      intensity: 'medium'
    },
    {
      id: 'clip-volley-003',
      title: 'Volley - Net Cam',
      description: 'Compact punch, early shoulder turn, and split-step sync.',
      angles: ['Net cam', 'Split-step trace', 'Reaction speed'],
      tags: ['volley', 'footwork'],
      source: 'Gemini',
      intensity: 'low'
    }
  ];

  feedbackCards: CoachAsset[] = [
    {
      id: 'feedback-serve-004',
      title: 'Serve Contact Point',
      description: 'Raise contact point by 6-8 inches; keep tossing arm longer for balance.',
      angles: ['Contact zone heatmap', 'Arm extension overlay'],
      tags: ['serve', 'balance'],
      source: 'Custom Model',
      intensity: 'medium'
    },
    {
      id: 'feedback-backhand-005',
      title: 'Backhand Load',
      description: 'Delay hip opening to keep the shoulder line closed through the load.',
      angles: ['Rear hip tracker', 'Load timing graph'],
      tags: ['backhand', 'coil'],
      source: 'Gemini',
      intensity: 'high'
    }
  ];

  uploadState: UploadProgress | null = null;
  analysisState: AnalysisProgressState | null = null;

  constructor(private mediaService: MediaLibraryService) {}

  ngOnInit(): void {
    this.mediaSub = this.mediaService.items$.subscribe(items => {
      if (items?.length) {
        this.videoClips = items
          .filter(item => item.mediaType === 'video' || item.mediaType === 'unknown')
          .map(item => this.mapMediaToClip(item));
        this.feedbackCards = items
          .filter(item => item.caption || item.description)
          .map(item => this.mapMediaToFeedback(item));
      }
    });

    void this.mediaService.loadAllMediaForUser();
  }

  ngOnDestroy(): void {
    this.mediaSub?.unsubscribe();
    this.analysisSub?.unsubscribe();
  }

  openUploadPicker(): void {
    this.uploadInput?.nativeElement?.click();
  }

  handleUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (this.uploadInput?.nativeElement) {
      this.uploadInput.nativeElement.value = '';
    }

    this.uploadState = { status: 'progress', percentage: 0 };
    const uploadSub = this.mediaService
      .uploadMediaWithProgress(file, { title: file.name })
      .subscribe({
        next: (progress) => {
          this.uploadState = progress;
          if (progress.status === 'complete' && progress.mediaId) {
            this.startAnalysisTracking([progress.mediaId]);
          }
        },
        error: (err: UploadProgress | Error) => {
          if (err && typeof err === 'object' && 'status' in err) {
            this.uploadState = err as UploadProgress;
          } else {
            this.uploadState = {
              status: 'error',
              percentage: 0,
              error: err instanceof Error ? err.message : 'Upload failed'
            };
          }
        },
        complete: () => uploadSub.unsubscribe()
      });
  }

  private startAnalysisTracking(jobIds: string[]): void {
    this.analysisSub?.unsubscribe();
    this.analysisSub = this.mediaService.watchAnalysisForJobIds(jobIds).subscribe({
      next: (state) => {
        this.analysisState = state;
      },
      error: () => {
        this.analysisSub?.unsubscribe();
        this.analysisSub = this.mediaService.pollAnalysisForJobIds(jobIds).subscribe({
          next: (state) => {
            this.analysisState = state;
          },
          error: (err) => {
            this.uploadState = {
              status: 'error',
              percentage: 0,
              error: err instanceof Error ? err.message : 'Analysis polling failed'
            };
          }
        });
      }
    });
  }

  private mapMediaToClip(item: MediaItem): CoachAsset {
    return {
      id: item.id,
      title: item.title || item.fileName || 'Untitled clip',
      description: item.description || item.caption || 'No description available yet.',
      angles: (item.labels || []).slice(0, 3),
      tags: (item.labels || []).slice(0, 4),
      source: item.caption ? 'Gemini' : 'Custom Model',
      intensity: item.status === 'processing' ? 'medium' : item.status === 'failed' ? 'low' : 'high'
    };
  }

  private mapMediaToFeedback(item: MediaItem): CoachAsset {
    return {
      id: `feedback-${item.id}`,
      title: item.title || item.fileName || 'Feedback insight',
      description: item.caption || item.description || 'Insight is still processing.',
      angles: (item.labels || []).slice(0, 2),
      tags: (item.labels || []).slice(0, 3),
      source: item.caption ? 'Gemini' : 'Custom Model',
      intensity: item.status === 'failed' ? 'low' : 'high'
    };
  }

  dropAsset(event: CdkDragDrop<CoachAsset[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );
  }

  trackById(index: number, asset: CoachAsset): string {
    return asset.id;
  }
}
